import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { resetAuthTokensClientSide } from '@/services/authApi/persist'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { Button } from 'ui/src/components/Button/Button'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { FullscreenDialog } from 'ui/src/components/Dialog/FullscreenDialog'
import { Text } from 'ui/src/components/Text/Text'
import { WarningTriangleIcon } from 'ui/src/icons/WarningTriangleIcon'
import { tokens } from 'ui/src/theme/tokens.css'
import { title } from './ChangeSsnWarningDialog.css'

type Props = {
  open: boolean
  onAccept?: (() => void) | (() => Promise<void>)
  onDecline: () => void
}

export const ChangeSsnWarningDialog = ({ open, onAccept, onDecline }: Props) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation(['common', 'purchase-form'])
  const { reset: resetShopSession } = useShopSession()

  const handleAccept = useCallback(async () => {
    setLoading(true)

    resetAuthTokensClientSide()

    await resetShopSession()

    if (onAccept) {
      await onAccept()
    }

    setLoading(false)
  }, [onAccept, resetShopSession])

  return (
    <Dialog.Root open={open} onOpenChange={onDecline}>
      <FullscreenDialog.Modal
        accessibleCloseLabel={t('common:DIALOG_CLOSE')}
        center={true}
        Footer={
          <>
            <Button size="large" loading={loading} onClick={handleAccept}>
              {t('purchase-form:CHANGE_SSN_BUTTON')}
            </Button>
            <Button variant="ghost" size="large" onClick={onDecline}>
              {t('DIALOG_BUTTON_CANCEL', { ns: 'common' })}
            </Button>
          </>
        }
      >
        <Dialog.Title>
          <Text className={title}>
            <WarningTriangleIcon size="1em" color={tokens.legacyColors.amber600} />
            {t('purchase-form:CHANGE_SSN_TITLE')}
          </Text>
        </Dialog.Title>
        <Text color="textSecondary" align="center">
          {t('purchase-form:CHANGE_SSN_DESCRIPTION')}
        </Text>
      </FullscreenDialog.Modal>
    </Dialog.Root>
  )
}
